import { useState, useEffect, useRef, useCallback } from "react";
import SectionHeading from "@/components/ui/SectionHeading";
import { useGsapScrollTrigger } from "@/hooks/useGsapScrollTrigger";

interface CollageImage {
  src: string;
  alt: string;
}

interface EditorialImageCollageProps {
  scriptAccent?: string;
  heading: string;
  body: string;
  images: CollageImage[];
  imagePosition?: "left" | "right";
  className?: string;
}

/** Auto-rotating image carousel for mobile */
function MobileRotatingImage({ images }: { images: CollageImage[] }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval>>(undefined);

  useEffect(() => {
    if (images.length <= 1) return;
    timerRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % images.length);
    }, 5000);
    return () => clearInterval(timerRef.current);
  }, [images.length]);

  const advance = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  if (!images[0]?.src) return null;

  return (
    <div
      className="collage-img relative aspect-[4/5] w-full overflow-hidden rounded-sm shadow-md lg:hidden"
      onClick={advance}
    >
      {images.map((img, i) => (
        <img
          key={i}
          src={img.src}
          alt={img.alt}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ease-in-out ${
            i === activeIndex ? "opacity-100" : "opacity-0"
          }`}
          loading="lazy"
          width={800}
          height={1000}
        />
      ))}
      {images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 gap-2">
          {images.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={(e) => { e.stopPropagation(); setActiveIndex(i); }}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                i === activeIndex
                  ? "w-5 bg-parchment/80"
                  : "w-1.5 bg-parchment/40"
              }`}
              aria-label={`View image ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function EditorialImageCollage({
  scriptAccent,
  heading,
  body,
  images,
  imagePosition = "right",
  className = "",
}: EditorialImageCollageProps) {
  const ref = useGsapScrollTrigger(
    (tl, el) => {
      // Animate text elements
      const textEls = el.querySelectorAll(".collage-text-animate");
      tl.fromTo(
        textEls,
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 0.8, stagger: 0.15, ease: "power2.out" },
        0,
      );

      // Staggered image reveal
      const imgEls = el.querySelectorAll(".collage-img");
      if (imgEls.length > 0) {
        tl.fromTo(
          imgEls,
          { opacity: 0, scale: 0.92 },
          {
            opacity: 1,
            scale: 1,
            duration: 1,
            stagger: 0.2,
            ease: "power2.out",
          },
          0.15,
        );
      }
    },
    { once: true },
  );

  const isLeft = imagePosition === "left";

  return (
    <section ref={ref} className={`px-6 py-24 lg:px-12 lg:py-40 ${className}`}>
      <div className="mx-auto grid max-w-7xl gap-16 lg:grid-cols-2 lg:items-center lg:gap-24">
        {/* Text column */}
        <div className={isLeft ? "lg:order-2" : ""}>
          <div className="collage-text-animate">
            <SectionHeading
              scriptAccent={scriptAccent}
              heading={heading}
              centered={false}
            />
          </div>
          <p className="collage-text-animate max-w-prose font-body text-lg leading-[1.9] text-soft-charcoal/80 lg:text-xl">
            {body}
          </p>
        </div>

        {/* Image collage column */}
        <div className={isLeft ? "lg:order-1" : ""}>
          {/* Desktop: editorial overlapping composition */}
          <div className="relative hidden min-h-[520px] lg:block lg:min-h-[600px]">
            {/* Image 1 — tall portrait, dominant anchor */}
            {images[0]?.src && (
            <div className="collage-img absolute w-[65%] overflow-hidden rounded-sm shadow-md z-20 left-[0%] top-[5%]">
              <img
                src={images[0].src}
                alt={images[0].alt}
                className="editorial-image aspect-[3/4] w-full object-cover"
                loading="lazy"
                width={660}
                height={880}
              />
            </div>
            )}

            {images[1]?.src && (
            <div className="collage-img absolute overflow-hidden rounded-sm shadow-sm z-20 right-[12%] top-[55%] w-[40%]">
              <img
                src={images[1].src}
                alt={images[1].alt}
                className="editorial-image w-full object-cover"
                loading="lazy"
                width={660}
                height={880}
              />
            </div>
            )}
          </div>

          {/* Mobile: rotating image carousel */}
          <MobileRotatingImage images={images} />
        </div>
      </div>
    </section>
  );
}
